import { store$, store } from '@redux/store';
import { map, filter, distinctUntilChanged, distinctUntilKeyChanged, withLatestFrom, fromEvent, take } from 'rxjs';
import objectEquals from 'utils/equals';
import { selectLocation, selectPaging, selectResultType } from 'features/searchResultsFacets';
import { isLoading } from 'features/utm';
import LoadingStatus from 'enums/loadingStatus';
import ResultPresentationType from 'layout/constants/resultPresentationType';
import { selectNativeAdInformation } from '../redux/selectors';

const footerAds = document.querySelectorAll('[data-footer-ad]');

/**
 * handleFooterAds: configures the footer ads's styles and data attributes.
 */
const handleFooterAds = () => {
  footerAds.forEach((element) => {
    element.dataset.native = 'true';
    element.classList.add('nhs-c-card--native-ad-fixed');
    element.setAttribute('hidden', '');
  });
};

/**
 * A one-time stream will happen in the load window event.
 * This is taken into consideration for the handleFooterAds subscriber.
 */
fromEvent(window, 'load').pipe(take(1)).subscribe(handleFooterAds);

/**
 * Sets the inner HTML of elements in the `footerAds` array to a specified template and
 * adds classes and attributes to them.
 * @param ads - object which is used to retrieve the template for the footer ads.
 *
 * Hides footer ads with a `data-native` attribute set to 'true'.
 */
const hideFooterAds = () =>
  footerAds.forEach((element) => {
    if (element.dataset.native === 'true') {
      element.setAttribute('hidden', '');
      delete element.dataset.loaded;
    }
  });

let previousLocation;
let previousResultType;
let previousPaging;
const refreshOrRegenerateNativeAds = () => {
  const state = store.getState();
  const currentLocation = selectLocation(state);
  const currentResultType = selectResultType(state);
  const currentPaging = selectPaging(state);
  if (window.AppNexus) {
    const locationChanged = previousLocation && !objectEquals(currentLocation, previousLocation);
    const resultTypeChanged = previousResultType && !objectEquals(currentResultType, previousResultType);
    const pagingChanged = previousPaging && !objectEquals(currentPaging, previousPaging);

    if (locationChanged || resultTypeChanged || pagingChanged) {
      window.AppNexus.refreshAdsAndUpdateParams();
    } else {
      window.AppNexus.regenerateAds(true);
    }
  }

  if (!window.AppNexus) {
    require('libraries/appNexus');
  }

  previousLocation = currentLocation;
  previousResultType = currentResultType;
  previousPaging = currentPaging;
};

/*
 * Observable to know when the listing is loaded
 */
const onResultLoad$ = store$.pipe(map(selectNativeAdInformation), distinctUntilKeyChanged('fetchResultListingsStatus'));

/**
 * Observale to check that the Results are ready.
 * Triggers the Native Ads insertion on the the SRP with pagination presentation type.
 * or Triggers the Native Ads insertion on the the SRP with Infinite scroll presentation type.
 */
onResultLoad$
  .pipe(filter(({ fetchResultListingsStatus }) => fetchResultListingsStatus === LoadingStatus.Succeeded))
  .subscribe(refreshOrRegenerateNativeAds);

onResultLoad$
  .pipe(
    filter(({ resultsPresentationType }) => resultsPresentationType === ResultPresentationType.Pagination.toString()),
    filter(({ fetchResultListingsStatus }) => fetchResultListingsStatus === LoadingStatus.Loading)
  )
  .subscribe(hideFooterAds);

/**
 * Observale to check when chaging from  Homes or Comms SRP to builder directory.
 */
onResultLoad$
  .pipe(
    filter(
      ({ resultsPresentationType }) => resultsPresentationType === ResultPresentationType.InfiniteScrolling.toString()
    ),
    filter(({ paging }) => paging.page === 1),
    filter(({ fetchResultListingsStatus }) => fetchResultListingsStatus === LoadingStatus.Loading)
  )
  .subscribe(hideFooterAds);

/**
 * Observale to check if the native ads information have changed.
 */
const nativeInformation$ = store$.pipe(map(selectNativeAdInformation), distinctUntilChanged());

/**
 * Observale to check the UTM parameters changes.
 */
const onUtmParameterReady$ = store$.pipe(map(isLoading), filter(Boolean), distinctUntilChanged());

/**
 * Observable for the UTM call ready and the ad state.
 */
onUtmParameterReady$
  .pipe(
    withLatestFrom(nativeInformation$),
    map(([, nativeInformation]) => nativeInformation),
    filter(
      ({ resultsPresentationType }) =>
        resultsPresentationType === ResultPresentationType.Pagination.toString() ||
        resultsPresentationType === ResultPresentationType.InfiniteScrolling.toString()
    )
  )
  .subscribe(refreshOrRegenerateNativeAds);
